import React from 'react';
import { format } from 'date-fns';
import { MatchPreviewCard } from '@/components/MatchPreviewCard';
import { isCompleted, isLive } from '@/constants/matchStatus';
import { OPENED_OUTSIDE_TOURNAMENT_STATE } from '@/utils/tournamentMatchLinkState';
import type { MatchOverviewMatch, MatchTournamentPrediction } from '@/types/matchOverview';

export interface TournamentMatchPreviewCardProps {
  tournament: {
    id: number;
    name: string;
  };
  match: MatchOverviewMatch;
  /**
   * The user's prediction for the match in this tournament. `undefined` for
   * tournaments the user has not joined; `null` when they have not predicted.
   */
  prediction?: MatchTournamentPrediction | null;
  testId?: string;
}

/**
 * One tournament containing the current match, on the standalone match page:
 * the tournament Matches tab card (`MatchPreviewCard` — same match row and
 * prediction / points strip) under a header with the tournament name. The
 * whole card opens the match inside the tournament, which then links to the
 * tournament's matches.
 */
export const TournamentMatchPreviewCard: React.FC<TournamentMatchPreviewCardProps> = ({
  tournament,
  match,
  prediction,
  testId,
}) => {
  // Scores are only meaningful once the match has kicked off (same rule as
  // the tournament Matches tab).
  const showScore = isCompleted(match.status) || isLive(match.status);

  return (
    <MatchPreviewCard
      matchId={match.id}
      tournamentId={String(tournament.id)}
      linkState={OPENED_OUTSIDE_TOURNAMENT_STATE}
      headerTitle={tournament.name}
      testId={testId}
      team1Name={match.team1}
      team2Name={match.team2}
      team1LogoUrl={match.team1_logo_url ?? undefined}
      team2LogoUrl={match.team2_logo_url ?? undefined}
      score1={showScore ? match.score_team1 : null}
      score2={showScore ? match.score_team2 : null}
      status={match.status}
      time={format(new Date(match.match_date), 'HH:mm')}
      venue={match.venue ?? undefined}
      elapsedMinutes={match.elapsed_minutes}
      elapsedExtraMinutes={match.elapsed_extra_minutes}
      elapsedUpdatedAt={match.elapsed_updated_at}
      userPrediction={prediction ? {
        predicted_score_team1: prediction.predictedScoreTeam1,
        predicted_score_team2: prediction.predictedScoreTeam2,
        predicted_outcome: prediction.predictedOutcome,
        points_earned: prediction.pointsEarned === null ? null : Number(prediction.pointsEarned),
      } : null}
    />
  );
};

export default TournamentMatchPreviewCard;
